import { ConfigFormHeader } from '@/components/ConfigFormHeader'
import { ConversationStyleConfig } from '@/types/configuration'
import { Box, Card, Checkbox, Group, NumberInput, Select, Stack, Text, Textarea, TextInput } from '@mantine/core'
import { useForm } from '@mantine/form'
import { useEffect, useState } from 'react'

interface ConversationStyleFormProps {
  data: ConversationStyleConfig | null
  onSave: (data: ConversationStyleConfig) => void
  isSaving: boolean
}

function greetingsToDraft(greetings: string[]): string {
  return greetings.join('\n')
}

function draftToGreetings(draft: string): string[] {
  return draft.split('\n').filter(line => line.trim())
}

export function ConversationStyleForm({ data, onSave, isSaving }: ConversationStyleFormProps) {
  const [footerDraft, setFooterDraft] = useState('')

  const form = useForm<ConversationStyleConfig>({
    initialValues: {
      tone: 'conversational',
      aiPersona: { name: '', useStaffName: false, staffMemberName: '' },
      emojiUsage: 'moderate',
      templates: { greetings: [] },
      messageDelay: { enabled: true, minDelayMs: 8000, maxDelayMs: 8000 },
    },
  })

  useEffect(() => {
    if (data) {
      form.setValues(data)
      setFooterDraft(greetingsToDraft(data.templates?.greetings ?? []))
    }
  }, [data])

  return (
    <form
      onSubmit={form.onSubmit(values =>
        onSave({
          ...values,
          templates: { ...values.templates, greetings: draftToGreetings(footerDraft) },
        }),
      )}
    >
      <Stack gap="md">
        <ConfigFormHeader
          onReset={() => {
            form.reset()
            setFooterDraft('')
          }}
          isSaving={isSaving}
        />
        <Card shadow="sm" padding="lg" radius="md" withBorder>
          <Stack gap="md">
            <Group grow>
              <Select
                label="Tone Style"
                data={[
                  { value: 'formal', label: 'Formal' },
                  { value: 'conversational', label: 'Conversational' },
                ]}
                {...form.getInputProps('tone')}
              />
              <Select
                label="Emoji Usage"
                data={[
                  { value: 'none', label: 'None' },
                  { value: 'minimal', label: 'Minimal' },
                  { value: 'moderate', label: 'Moderate' },
                  { value: 'frequent', label: 'Frequent' },
                ]}
                {...form.getInputProps('emojiUsage')}
              />
            </Group>
          </Stack>
        </Card>

        <Card shadow="sm" padding="lg" radius="md" withBorder>
          <Stack gap="md">
            <Text fw={600}>AI Persona</Text>
            <TextInput
              label="AI Persona Name"
              placeholder="e.g., Jamie Sparks"
              description="The name the AI will use when introducing itself to customers"
              {...form.getInputProps('aiPersona.name')}
            />
          </Stack>
        </Card>

        <Card shadow="sm" padding="lg" radius="md" withBorder>
          <Stack gap="md">
            <Text fw={600}>Welcome message footer</Text>
            <Textarea
              label="Opt-in and disclosure text"
              description="For SMS first contact, this text is added after the AI’s personalized welcome—use it for opt-in language and legal disclosures. Each non-empty line becomes a separate paragraph. If you leave this blank, default disclosure wording is used where applicable."
              placeholder="Msg & data rates may apply. Reply STOP to opt out, HELP for help, YES to start the conversation."
              autosize
              minRows={6}
              maxRows={16}
              value={footerDraft}
              onChange={e => setFooterDraft(e.currentTarget.value)}
            />
          </Stack>
        </Card>

        <Card shadow="sm" padding="lg" radius="md" withBorder>
          <Stack gap="md">
            <Text fw={600}>Response Pacing</Text>
            <Checkbox
              label="Use natural response timing"
              description="Replies feel like a real person typing, not instant."
              {...form.getInputProps('messageDelay.enabled', { type: 'checkbox' })}
            />
            {form.values.messageDelay.enabled && (
              <Box style={{ width: 400, marginLeft: 32 }}>
                <NumberInput
                  label="Delay (seconds)"
                  description="How long to wait before each reply is shown."
                  min={0}
                  max={60}
                  step={0.5}
                  value={form.values.messageDelay.minDelayMs / 1000}
                  onChange={value => {
                    const ms = (value as number) * 1000
                    form.setFieldValue('messageDelay.minDelayMs', ms)
                    form.setFieldValue('messageDelay.maxDelayMs', ms)
                  }}
                />
              </Box>
            )}
          </Stack>
        </Card>
      </Stack>
    </form>
  )
}
